import { useEffect, useState, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Loader from "../Loader";
import ErrorMsg from "../ErrorMsg";
import StatTile from "./StatTile";
import BarrasCategoria from "./BarrasCategoria";
import BarrasRampa from "./BarrasRampa";
import Embudo from "./Embudo";
import TendenciaChart from "./TendenciaChart";
import AgendaTable, { SEMAFORO_UMBRAL } from "./AgendaTable";
import TablaDeDatos from "./TablaDeDatos";
import EstadoVacio from "./EstadoVacio";
import { fmt, sumaTotal, redondear1 } from "./fmt";
import { IniciativasIAServices } from "../../services/iniciativasIA";
import "../../css/brand-tokens.css";
import "../../css/dashboardIA.css";

// Tablero de Control del Comité de IA (spec 010). Cambio de alcance 2026-08-09 (C-1): DEJÓ de ser una
// vista con ruta propia -- ahora es la 4.ª pestaña de `views/IniciativasIA.jsx`, montada con
// `mountOnEnter` (el fetch de este componente solo corre cuando el usuario abre la pestaña). Por eso NO
// trae `Layout`/`TitleCommon`/`Container`: los pone el módulo. Tampoco vuelve a pedir `catalogos` ni
// `champions` -- el módulo ya los tiene en Redux (`fetchCatalogos`/`fetchChampions`) y los pasa por props;
// pedirlos de nuevo aquí sería una llamada duplicada cada vez que se abre la pestaña.
//
// RF-3/RF-3a: restringido a Champions y Administradores. La autoridad es el 403 del servidor -- este
// componente solo se MONTA si el padre ya decidió `esChampion || esAdmin` (cosmético, RF-3b), pero aun
// así maneja el 403 explícitamente (RF-3c/T-C2-3): nunca pantalla en blanco ni error crudo.
//
// D-5: el índice de recordsets ya murió en el controlador de la API — este componente consume
// `data.titulares`, `data.embudo`, etc. por nombre, nunca por posición.

const FILTROS_INICIALES = { meses: "", clubhouse: "", cluster: "", champion: "" };

const DashboardComiteIA = ({ catalogos, champions, onVerDetalle, onIrARegistro }) => {
  const [t] = useTranslation("dashboardIA");
  const [tIni] = useTranslation("iniciativasIA"); // reusa iniciativasIA_tier_0..4 (RF-17-bis, no se duplica)

  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);
  const [prohibido, setProhibido] = useState(false); // 403 (T-C2-3): distinto del error genérico

  const clubhousesFiltro = (catalogos && catalogos.clubhouses) || [];
  const clustersFiltro = (catalogos && catalogos.clusters) || [];
  const championsFiltro = champions || [];

  const cargarDashboard = useCallback((filtrosActuales) => {
    setCargando(true);
    IniciativasIAServices.getDashboard(filtrosActuales)
      .then((resultado) => {
        setData(resultado);
        setError(false);
        setProhibido(false);
      })
      .catch((err) => {
        const status = err && err.response && err.response.status;
        setProhibido(status === 403);
        setError(status !== 403);
      })
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    cargarDashboard(FILTROS_INICIALES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiarFiltro = (campo) => (evento) => {
    const nuevosFiltros = { ...filtros, [campo]: evento.target.value };
    setFiltros(nuevosFiltros);
    cargarDashboard(nuevosFiltros);
  };

  const limpiarFiltros = () => {
    setFiltros(FILTROS_INICIALES);
    cargarDashboard(FILTROS_INICIALES);
  };

  // Carga inicial: sin render previo que atenuar, se muestra el loader de pantalla completa que ya usa
  // el resto del producto (mismo patrón que las otras 3 pestañas del módulo).
  if (cargando && !data && !error && !prohibido) {
    return <Loader />;
  }

  // RF-3c/T-C2-3: el 403 nunca se confunde con un error de carga genérico -- mensaje propio de "sin
  // permiso", no una pantalla en blanco ni el error crudo del servidor.
  if (prohibido) {
    return (
      <div className="pt-4">
        <ErrorMsg title="¡Ups!" msg={t("dashboardIA_error_forbidden")} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="pt-4">
        <ErrorMsg title="¡Ups!" msg={t("dashboardIA_error_load")} />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const titulares = data.titulares || {};
  const totalRegistradas = Number(titulares.Total) || 0;

  const filtrosActivos = !!(filtros.meses || filtros.clubhouse || filtros.cluster || filtros.champion);
  // Decisión de vacío tomada UNA sola vez, aquí (plan.md §3.3): §3.10 del spec — vacío total (RF-27a) vs.
  // vacío por filtro (RF-27b) son visualmente iguales (Total === 0) y significan cosas distintas, así que
  // se distinguen por si hay o no un filtro activo.
  const vacioTotal = totalRegistradas === 0 && !filtrosActivos;
  const vacioPorFiltro = totalRegistradas === 0 && filtrosActivos;

  if (vacioTotal) {
    // RF-27a: un solo vacío a página completa, CTA a la pestaña de registro, filtros OCULTOS — no seis
    // tarjetas en cero. Ya no navega a otra ruta (C-1): cambia a la pestaña "Iniciativas" del mismo módulo.
    return (
      <div className="dia-page">
        <EstadoVacio
          variante="pagina"
          icon="fa-brain"
          titulo={t("dashboardIA_vacio_total_titulo")}
          descripcion={t("dashboardIA_vacio_total_desc")}
          accion={{
            texto: t("dashboardIA_vacio_total_cta"),
            icon: "fa-plus",
            onClick: onIrARegistro,
          }}
        />
      </div>
    );
  }

  // --- Preparación de datos de los bloques 2..6 (RF-27c/RF-27f: la suma decide el vacío de tarjeta,
  // nunca `.length` — el SP siempre manda el catálogo completo con Total = 0). --------------------------
  const embudo = data.embudo || [];
  const estados = data.estados || [];
  const clubhouse = data.clubhouse || [];
  const tipoIA = data.tipoIA || [];
  const estrategia = data.estrategia || [];
  const tier = data.tier || [];
  const checklist = data.checklist || [];
  const vigilancia = data.vigilancia || [];
  const tendencia = data.tendencia || [];
  const championsData = data.champions || [];
  const agenda = data.agenda || [];

  const enviadasEmbudo = embudo.find((p) => p.Paso === "ENVIADAS");
  const devueltasEstado = estados.find((e) => e.Codigo === "DEVUELTA");
  const tasaDevolucion =
    enviadasEmbudo && enviadasEmbudo.Valor > 0 && devueltasEstado
      ? Math.round((devueltasEstado.Total / enviadasEmbudo.Valor) * 100)
      : null;

  const tierConNombre = tier.map((fila) => ({
    Nombre: tIni(`iniciativasIA_tier_${fila.Tier}`),
    Total: fila.Total,
    Tier: fila.Tier,
  }));
  const tierRefuerzo = tier.filter((fila) => fila.Tier === 3 || fila.Tier === 4);

  const tendenciaConEtiqueta = tendencia.map((fila) => ({
    ...fila,
    Etiqueta: `${t(`dashboardIA_mes_${fila.Mes}`)}-${String(fila.Anio).slice(2)}`,
  }));

  const championsConEtiqueta = championsData.map((champion) => ({
    Nombre: champion.Nombre,
    Habilitadas: champion.Habilitadas,
    Etiqueta: `${champion.Habilitadas} · ${fmt(redondear1(champion.DiasPromedio))}`,
  }));

  const pctF3 = totalRegistradas > 0 ? Math.round(((titulares.F3 || 0) / totalRegistradas) * 100) : null;
  const diasEnColaMax = agenda.length ? Math.max(...agenda.map((f) => f.DiasEnCola || 0)) : null;

  return (
    <div className="dia-page">
      <div className="dia-page__header">
        <p className="dia-page__subtitle">{t("dashboardIA_subtitle")}</p>
      </div>

      {/* RF-4: una única fila de filtros, arriba de todo. RF-27b: se mantiene visible y con su
          selección puesta cuando el filtro no devuelve nada. */}
      <div className="dia-filtros">
        <span className="dia-filtros__campo">
          <label htmlFor="dia-filtro-periodo">{t("dashboardIA_filtros_periodo_label")}</label>
          <select
            id="dia-filtro-periodo"
            className="form-control form-control-sm"
            value={filtros.meses}
            onChange={cambiarFiltro("meses")}
          >
            <option value="">{t("dashboardIA_filtros_periodo_todo")}</option>
            <option value="3">{t("dashboardIA_filtros_periodo_3m")}</option>
            <option value="6">{t("dashboardIA_filtros_periodo_6m")}</option>
            <option value="12">{t("dashboardIA_filtros_periodo_12m")}</option>
          </select>
        </span>
        <span className="dia-filtros__campo">
          <label htmlFor="dia-filtro-clubhouse">{t("dashboardIA_filtros_clubhouse_label")}</label>
          <select
            id="dia-filtro-clubhouse"
            className="form-control form-control-sm"
            value={filtros.clubhouse}
            onChange={cambiarFiltro("clubhouse")}
          >
            <option value="">{t("dashboardIA_filtros_todos")}</option>
            {clubhousesFiltro.map((c) => (
              <option key={c.IdClubhouse} value={c.IdClubhouse}>
                {c.Nombre}
              </option>
            ))}
          </select>
        </span>
        <span className="dia-filtros__campo">
          <label htmlFor="dia-filtro-cluster">{t("dashboardIA_filtros_cluster_label")}</label>
          <select
            id="dia-filtro-cluster"
            className="form-control form-control-sm"
            value={filtros.cluster}
            onChange={cambiarFiltro("cluster")}
          >
            <option value="">{t("dashboardIA_filtros_todos")}</option>
            {clustersFiltro.map((c) => (
              <option key={c.IdCluster} value={c.IdCluster}>
                {c.Nombre}
              </option>
            ))}
          </select>
        </span>
        <span className="dia-filtros__campo">
          <label htmlFor="dia-filtro-champion">{t("dashboardIA_filtros_champion_label")}</label>
          <select
            id="dia-filtro-champion"
            className="form-control form-control-sm"
            value={filtros.champion}
            onChange={cambiarFiltro("champion")}
          >
            <option value="">{t("dashboardIA_filtros_todos")}</option>
            {championsFiltro.map((c) => (
              <option key={c.IdUsuario} value={c.IdUsuario}>
                {c.NombreCompleto}
              </option>
            ))}
          </select>
        </span>
        {filtrosActivos && (
          <button type="button" className="btn btn-link dia-filtros__limpiar" onClick={limpiarFiltros}>
            <i className="fa fa-times mr-1" aria-hidden="true"></i>
            {t("dashboardIA_filtros_limpiar")}
          </button>
        )}
      </div>

      {/* RF-27b: vacío por filtro — filtros visibles arriba, cuerpo con el mensaje de "esa combinación"
          y el botón "Limpiar filtros". */}
      {vacioPorFiltro ? (
        <EstadoVacio
          variante="pagina"
          icon="fa-filter"
          titulo={t("dashboardIA_vacio_filtro_titulo")}
          descripcion={t("dashboardIA_vacio_filtro_desc")}
          accion={{ texto: t("dashboardIA_filtros_limpiar"), icon: "fa-times", onClick: limpiarFiltros }}
        />
      ) : (
        <div className={cargando ? "dia-atenuado" : ""}>
          {/* ============ 1 · Titulares ============ */}
          <section className="dia-seccion">
            <h2 className="dia-seccion__titulo">{t("dashboardIA_bloque1_titulo")}</h2>
            <Row>
              <Col md={3} className="mb-3">
                <StatTile
                  icono="fa-layer-group"
                  label={t("dashboardIA_titular_total")}
                  valor={fmt(totalRegistradas)}
                />
              </Col>
              <Col md={3} className="mb-3">
                <StatTile
                  icono="fa-rocket"
                  label={t("dashboardIA_titular_f3")}
                  valor={fmt(titulares.F3)}
                  sub={pctF3 == null ? undefined : t("dashboardIA_titular_f3_sub", { pct: pctF3 })}
                  tono="ok"
                />
              </Col>
              <Col md={3} className="mb-3">
                <StatTile
                  icono="fa-hourglass-half"
                  label={t("dashboardIA_titular_pendientes")}
                  valor={fmt(titulares.Pendientes)}
                  sub={
                    diasEnColaMax == null
                      ? undefined
                      : t("dashboardIA_titular_pendientes_sub", { dias: diasEnColaMax })
                  }
                  tono={diasEnColaMax != null && diasEnColaMax > SEMAFORO_UMBRAL.atrasado ? "alert" : "warn"}
                />
              </Col>
              <Col md={3} className="mb-3">
                <StatTile
                  icono="fa-stopwatch"
                  label={t("dashboardIA_titular_dias_promedio")}
                  valor={fmt(redondear1(titulares.DiasPromedio))}
                  sub={t("dashboardIA_titular_dias_promedio_sub", { meta: SEMAFORO_UMBRAL.enPlazo })}
                  tono="info"
                />
              </Col>
            </Row>
          </section>

          {/* ============ 2 · Embudo y estados ============ */}
          <section className="dia-seccion">
            <h2 className="dia-seccion__titulo">{t("dashboardIA_bloque2_titulo")}</h2>
            <Row>
              <Col lg={6} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque2_embudo_titulo")}</h3>
                  <p className="dia-card__subtitulo">{t("dashboardIA_bloque2_embudo_subtitulo")}</p>
                  <Embudo pasos={embudo} />
                  <TablaDeDatos
                    columnas={[
                      { key: "NombrePaso", label: t("dashboardIA_tabla_col_paso") },
                      { key: "Valor", label: t("dashboardIA_tabla_col_valor") },
                    ]}
                    filas={embudo.map((p) => ({ NombrePaso: t(`dashboardIA_embudo_paso_${p.Paso}`), Valor: p.Valor }))}
                  />
                </div>
              </Col>
              <Col lg={6} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque2_estados_titulo")}</h3>
                  <p className="dia-card__subtitulo">
                    {t("dashboardIA_bloque2_estados_subtitulo", {
                      tasa: tasaDevolucion == null ? "—" : `${tasaDevolucion}%`,
                    })}
                  </p>
                  {sumaTotal(estados) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <>
                      <BarrasCategoria datos={estados} nombreKey="Nombre" valorKey="Total" />
                      <TablaDeDatos
                        columnas={[
                          { key: "Nombre", label: t("dashboardIA_tabla_col_nombre") },
                          { key: "Total", label: t("dashboardIA_tabla_col_valor") },
                        ]}
                        filas={estados}
                      />
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </section>

          {/* ============ 3 · Composición ============ */}
          <section className="dia-seccion">
            <h2 className="dia-seccion__titulo">{t("dashboardIA_bloque3_titulo")}</h2>
            <Row>
              <Col lg={4} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque3_clubhouse")}</h3>
                  {sumaTotal(clubhouse) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <>
                      <BarrasCategoria datos={clubhouse} height={260} />
                      <TablaDeDatos
                        columnas={[
                          { key: "Nombre", label: t("dashboardIA_tabla_col_nombre") },
                          { key: "Total", label: t("dashboardIA_tabla_col_valor") },
                        ]}
                        filas={clubhouse}
                      />
                    </>
                  )}
                </div>
              </Col>
              <Col lg={4} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque3_tipoia")}</h3>
                  {sumaTotal(tipoIA) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <>
                      <BarrasCategoria datos={tipoIA} height={260} />
                      <TablaDeDatos
                        columnas={[
                          { key: "Nombre", label: t("dashboardIA_tabla_col_nombre") },
                          { key: "Total", label: t("dashboardIA_tabla_col_valor") },
                        ]}
                        filas={tipoIA}
                      />
                    </>
                  )}
                </div>
              </Col>
              <Col lg={4} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque3_estrategia")}</h3>
                  {sumaTotal(estrategia) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <>
                      <BarrasCategoria datos={estrategia} height={260} />
                      <TablaDeDatos
                        columnas={[
                          { key: "Nombre", label: t("dashboardIA_tabla_col_nombre") },
                          { key: "Total", label: t("dashboardIA_tabla_col_valor") },
                        ]}
                        filas={estrategia}
                      />
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </section>

          {/* ============ 4 · Gobernanza y riesgo de IA ============ */}
          <section className="dia-seccion">
            <h2 className="dia-seccion__titulo">{t("dashboardIA_bloque4_titulo")}</h2>
            <Row>
              <Col lg={6} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque4_tier_titulo")}</h3>
                  <p className="dia-card__subtitulo">{t("dashboardIA_bloque4_tier_subtitulo")}</p>
                  {sumaTotal(tier) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <>
                      <BarrasRampa datos={tierConNombre} anchoEtiquetas={60} />
                      {/* RF-16: tiers 3 y 4 con ícono + etiqueta, nunca solo el color de la rampa. */}
                      <div className="dia-tier-alerta">
                        {tierRefuerzo.map((fila) => (
                          <span key={fila.Tier} className="dia-badge dia-badge--alert dia-badge--bajo">
                            <i className="fa fa-triangle-exclamation" aria-hidden="true"></i>
                            <span>
                              {tIni(`iniciativasIA_tier_${fila.Tier}`)} · {t("dashboardIA_bloque4_tier_refuerzo")} (
                              {fila.Total})
                            </span>
                          </span>
                        ))}
                      </div>
                      <TablaDeDatos
                        columnas={[
                          { key: "Nombre", label: t("dashboardIA_tabla_col_nombre") },
                          { key: "Total", label: t("dashboardIA_tabla_col_valor") },
                        ]}
                        filas={tierConNombre}
                      />
                    </>
                  )}
                </div>
              </Col>
              <Col lg={6} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque4_checklist_titulo")}</h3>
                  <p className="dia-card__subtitulo">
                    {t("dashboardIA_bloque4_checklist_subtitulo", { total: totalRegistradas })}
                  </p>
                  {sumaTotal(checklist) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <BarrasCategoria datos={checklist} height={150} />
                  )}

                  <h3 className="dia-card__titulo mt-3">{t("dashboardIA_bloque4_vigilancia_titulo")}</h3>
                  {sumaTotal(vigilancia) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <BarrasCategoria datos={vigilancia} height={130} />
                  )}
                  <TablaDeDatos
                    columnas={[
                      { key: "Nombre", label: t("dashboardIA_tabla_col_nombre") },
                      { key: "Total", label: t("dashboardIA_tabla_col_valor") },
                    ]}
                    filas={[...checklist, ...vigilancia]}
                  />
                </div>
              </Col>
            </Row>
          </section>

          {/* ============ 5 · Salud del proceso ============ */}
          <section className="dia-seccion">
            <h2 className="dia-seccion__titulo">{t("dashboardIA_bloque5_titulo")}</h2>
            <Row>
              <Col lg={6} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque5_tendencia_titulo")}</h3>
                  <p className="dia-card__subtitulo">{t("dashboardIA_bloque5_tendencia_subtitulo")}</p>
                  {sumaTotal(tendencia) === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <>
                      <TendenciaChart datos={tendenciaConEtiqueta} />
                      <TablaDeDatos
                        columnas={[
                          { key: "Etiqueta", label: t("dashboardIA_tabla_col_mes") },
                          { key: "Total", label: t("dashboardIA_tabla_col_valor") },
                        ]}
                        filas={tendenciaConEtiqueta}
                      />
                    </>
                  )}
                </div>
              </Col>
              <Col lg={6} className="mb-3">
                <div className="dia-card">
                  <h3 className="dia-card__titulo">{t("dashboardIA_bloque5_champions_titulo")}</h3>
                  <p className="dia-card__subtitulo">{t("dashboardIA_bloque5_champions_subtitulo")}</p>
                  {sumaTotal(championsConEtiqueta, "Habilitadas") === 0 ? (
                    <EstadoVacio variante="tarjeta" titulo={t("dashboardIA_vacio_tarjeta_titulo")} />
                  ) : (
                    <>
                      <BarrasCategoria
                        datos={championsConEtiqueta}
                        valorKey="Habilitadas"
                        labelKey="Etiqueta"
                        height={Math.max(150, championsConEtiqueta.length * 42)}
                      />
                      <TablaDeDatos
                        columnas={[
                          { key: "Nombre", label: t("dashboardIA_tabla_col_nombre") },
                          { key: "Etiqueta", label: t("dashboardIA_bloque5_champions_col_etiqueta") },
                        ]}
                        filas={championsConEtiqueta}
                      />
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </section>

          {/* ============ 6 · Mesa de decisión ============ */}
          <section className="dia-seccion">
            <h2 className="dia-seccion__titulo">{t("dashboardIA_bloque6_titulo")}</h2>
            <div className="dia-card">
              <h3 className="dia-card__titulo">{t("dashboardIA_bloque6_subtitulo")}</h3>
              <AgendaTable filas={agenda} onVerDetalle={onVerDetalle} />
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default DashboardComiteIA;
